import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";

import { getServices, deleteService, restoreService } from "api/Services";
import { trackActivity } from "api/ActivityTracker";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import { PlusIcon, QuestionMarkIcon, TrashIcon, CheckMarkIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import ServicePricingImportModal from "components/ServicePricingImportModal";
import P from "components/typography/P";
import moment from "moment-timezone";
import { Badge, Box } from "theme-ui";
import { themevals } from "theme/themevals";
import UserContext from "context/User";

const columnHelper = createColumnHelper();

const createColumn = (id: string, title: string, accessor: (row: any) => any, cell?: (info: any) => any, options = {}) =>
  columnHelper.accessor(accessor, { id, cell: cell || ((info) => info.getValue()), header: () => <span>{title}</span>, ...options });

export default function Services () {
  const navigate = useNavigate();

  const [refreshData, setRefreshData] = useState<boolean>(false);

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [showImport, setShowImport] = useState<boolean>(false);

  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const lastTrackedQuery = useRef<string | null>(null);

  const confirmDelete = async (del: any) => {
    setConfirmSubmit(true);
    const res = await deleteService({ data: del });
    if (res?.success) {
      toast.add("Service deactivated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "services",
        action: "deactivate_service",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "service",
        entityId: del?.id ?? null,
        metadata: {
          source: "service_list",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setRefreshData(true);
      setTimeout(() => { setRefreshData(false); }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error deleting service.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const confirmReactivate = async (service: any) => {
    setConfirmSubmit(true);
    try {
      const res = await restoreService({ id: service?.id });
      if (res?.success) {
        toast.add("Service reactivated.", "var(--theme-ui-colors-success)");
        void trackActivity({
          module: "services",
          action: "reactivate_service",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "service",
          entityId: service?.id ?? null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setRefreshData(true);
        setTimeout(() => { setRefreshData(false); }, 100);
      } else {
        toast.add(res?.message ? res.message : "Error reactivating service.", "var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message ? error.message : "Error reactivating service.", "var(--theme-ui-colors-danger)");
    } finally {
      setConfirmSubmit(false);
      setConfirmModal(false);
    }
  };

  const deleteModal = async (del?: any) => {
    setConfirmModal(
      <BasicModal
        title="Deactivate service?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={(
          <>
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => confirmDelete(del)}
              $submitting={confirmSubmit}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
            >
              {!confirmSubmit && <TrashIcon fill={"#fff"} />}
              {"Deactivate"}
            </BasicButton>
          </>
        )}
      >
        <P lineHeight={"22px"}>Are you sure you want to deactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{del?.name}</strong>?</P>
      </BasicModal>
    );
  };

  const submitReactivate = async (service?: any) => {
    setConfirmModal(
      <BasicModal
        title="Reactivate service?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={(
          <BasicButton
            type="button"
            onClick={() => confirmReactivate(service)}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{
              margin: "0",
              backgroundColor: themevals.colors.success,
              borderColor: themevals.colors.success,
              color: "#fff",
            }}
          >
            {!confirmSubmit && <CheckMarkIcon fill={"#fff"} />}
            Reactivate
          </BasicButton>
        )}
      >
        <P lineHeight={"22px"}>Are you sure you want to reactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{service?.name}</strong>?</P>
      </BasicModal>
    );
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Services";
  }, []);

  return (
    <>
      <Header></Header>
      <Page title="Services" subtitle="Manage services for your company">
        <TableData
          api={getServices}
          nothingFoundText="No services found."
          columns={[
            createColumn("name", "Name", (row) => row?.name, (info) => {
              const service = info.row.original;
              const isDeleted = Boolean(service?.deletedAt) || service?.status === 0;
              return (
                <Box
                  sx={{ color: "#000", cursor: isDeleted ? "default" : "pointer" }}
                  onClick={() => {
                    if (!isDeleted) {
                      navigate(`/services/${service.id}`);
                    }
                  }}
                >
                  {info.getValue()}
                </Box>
              );
            }),
            createColumn("description", "Description", (row) => row?.description),
            createColumn("deletedAt", "Status", (row) => row?.status, (info) => (() => {
              switch (info.getValue()) {
                case 1:
                  return <Badge as="span" variant="success">Active</Badge>;
                default:
                  return <Badge as="span" variant="danger">Inactive</Badge>;
              }
            })()),
            createColumn("updatedAt", "Last Updated", (row) => row?.updatedAt, (info) => moment(info.getValue()).format("MMM D, YYYY")),
            createColumn("createdAt", "Date Added", (row) => row?.createdAt, (info) => moment(info.getValue()).format("MMM D, YYYY")),
            createColumn("lastUpdateBy", "Updated By", (row) => row?.lastUpdateBy, (info) => `${info?.getValue()?.firstName || ""} ${info?.getValue()?.lastName || ""}`),
            createColumn("actions", "", () => "actions", (info) => {
              const service = info.row.original;
              const isDeleted = Boolean(service?.deletedAt) || service?.status === 0;
              const items = isDeleted
                ? [
                    {
                      label: "Reactivate",
                      onClick: () => submitReactivate(service),
                      style: { color: themevals.colors.success },
                    },
                  ]
                : [
                    { label: "Edit", onClick: () => navigate(`/services/${service.id}`) },
                    { label: "Deactivate", onClick: () => deleteModal(service), style: { color: "var(--theme-ui-colors-danger)" } },
                  ];

              return (
                <Box>
                  <DropDownMenu
                    toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />}
                    items={items}
                  />
                </Box>
              );
            }, { header: () => null, enableSorting: false, meta: { style: { width: "10px" } } }),
          ]}
          refresh={refreshData}
          returnQuery={(query: any) => {
            if (!query) return;

            const signature = JSON.stringify({
              page: query?.pageNum ?? null,
              limit: query?.recordsPer ?? null,
            });

            if (lastTrackedQuery.current === signature) {
              return;
            }
            lastTrackedQuery.current = signature;

            void trackActivity({
              module: "services",
              action: "view_service_list",
              actionType: "view",
              userId: user?.id || null,
              companyId: user?.company?.id || null,
              entityType: "service_list",
              entityId: null,
              metadata: {
                page: query?.pageNum ?? null,
                limit: query?.recordsPer ?? null,
              },
              userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            });
          }}
          actions={[
            <BasicButton key="import" scheme="secondary" outline onClick={() => setShowImport(true)} styles={{ marginRight: "8px" }}>Import pricing</BasicButton>,
            <BasicButton key="1" onClick={() => navigate("/services/create")} $iconHeight="10px"><PlusIcon fill="#fff" />Add service</BasicButton>,
          ]}
          title={"All Services"}
        />
      </Page>
      {confirmModal}
      {showImport && (
        <ServicePricingImportModal
          title="Import service pricing"
          onClose={() => setShowImport(false)}
          onCommitted={() => {
            setRefreshData(true);
            setTimeout(() => { setRefreshData(false); }, 100);
          }}
        />
      )}
    </>
  );
}
